/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 7/20/2023
 */
import React, { useEffect, useState } from "react"
import { Box, Button, IconButton, Tooltip, MenuItem, ListItemIcon } from "@mui/material"
import api from "../../../../base/services/ResApi"
import { FetchErrorHandeling } from "../../../../base/errorHandeling/FetchErrorHandeling"
import Alerts from "../../../../base/errorHandeling/Alerts"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"
import DeleteIcon from "@mui/icons-material/Delete"
import DeleteModal from "../../../components/DeleteModals/DeleteModal"
import { RequestErrorHandeling } from "../../../../base/errorHandeling/RequestErrorHandeling"
import TerminalsColumns from "./TerminalsColumns"
import ApproveTerminalsMoreInfoModal from "./ApproveTerminalsMoreInfoModal"
import SecurityUpdateWarningIcon from "@mui/icons-material/SecurityUpdateWarning"
import UpdateTmnlStatusModal from "./UpdateTmnlStatusModal"
import TerminalAssignmentModal from "./TerminalAssignmentModal"
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined"

const ApprovedTerminals = ({
  updateData,
  setUpdateData,
  isSearch,
  setIsSearch,
  filterType,
  filterByVal,
  ltsAppStatus,
  applicationId = "",
}) => {
  const [approvedData, setApprovedData] = useState([])
  const [transformedData, setTransformedData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [rowsData, setRowsData] = useState([])
  const [isStatusChange, setIsStatusChange] = useState(false)

  //model States
  const [deleteModelOpen, setDeleteModelOpen] = useState(false)
  const [isBtnLoading, setIsBtnLoading] = useState(false)

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false)
  const [successMessages, setSuccessMessages] = useState([])

  //for error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  //table state
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [appRowCount, setAppRowCount] = useState(0)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  // set open modals
  const [moreInfoModalOpen, setMoreInfoModalOpen] = useState(false)
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false)
  const [rowData, setRowData] = useState([])
  const [tableState, setTableState] = useState()
  const [assignmentTerminals, setAssignmentTerminals] = useState(null)

  // table data loading
  let alertProps = {}
  if (iserror) {
    alertProps = {
      color: "error",
      children: "Error loading data",
    }
  }

  const columns = TerminalsColumns()

  //Approve Data fetch
  useEffect(() => {
    const fetchData = async () => {
      // Guard every fetch path, including pagination and refreshes.
      if (ltsAppStatus && !applicationId.trim()) {
        setIsSearch(false)
        return
      }
      setIsLoading(true)

      const apprvConfig = {
        headers: {
          Action: "V_ALL_APPR_TMNL",
          "Sort-Column": "tmnl_id",
          "Sort-Order": "asc",
          Filter: filterType,
          Value: filterByVal,
          "Page-Number": pagination.pageIndex + 1,
          "Page-Size": pagination.pageSize,
          "Is-Pagination": "true",
          "X-Additional-Info":true,
          ...(ltsAppStatus ? {
            "Latest-App-Status": ltsAppStatus,
            "Application-Id": applicationId.trim(),
          } : {}),
        },
      }

      try {
        const response = await api.get("/terminals", apprvConfig)
        const resData = response.data?.dataList || []

        setApprovedData(resData)
        setAppRowCount(response.data?.total || 0)

        setErrorMessages([])
        setIserror(false)
        setIsUnauhtError(false)
      } catch (error) {
        FetchErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
      } finally {
        setIsLoading(false)
        setIsSearch(false)
        setUpdateData(false)
      }
    }

    if (
      (isSearch ||
        isStatusChange ||
        updateData ||
        pagination.pageIndex ||
        pagination.pageSize) &&
      (filterType || ltsAppStatus)
    ) {
      fetchData()
    } else {
      setIsSearch(false)
    }
  }, [
    updateData,
    isUnauhtError,
    pagination.pageIndex,
    pagination.pageSize,
    isSearch,
  ])

  //remove error message after some time
  useEffect(() => {
    if (iserror === true) {
      setTimeout(() => {
        setIserror(false)
        setErrorMessages([])
      }, 10000)
    }
  }, [iserror])

  //remove success message after some time
  useEffect(() => {
    if (isSuccess === true) {
      setTimeout(() => {
        setIsSuccess(false)
        setSuccessMessages([])
        setDeleteModelOpen(false)
      }, 3000)
    }
  }, [isSuccess])

  const selectList = rowsData?.map((row) => ({ terminalId: row.terminalId }))

  // delete model api
  const handleDelete = () => {
    setIsBtnLoading(true)

    let delConfig = {
      headers: {
        "Content-Type": "application/json",
      },
    }
    const terminalIdsToDelete = selectList.map((item) => item.terminalId)
    const payload = {
      data: { deletedTerminalIdList: terminalIdsToDelete },
    }

    api
      .delete("/terminals", payload, delConfig)
      .then((res) => {
        tableState.resetRowSelection()
        setDeleteModelOpen(false)
        setIserror(false)
        setErrorMessages([])
        setIsUnauhtError(false)
        setIsSuccess(true)
        setSuccessMessages([res.data?.description])
        setUpdateData(true)
        setIsBtnLoading(false)
      })
      .catch((error) => {
        setIsBtnLoading(false)
        setIsSuccess(false)
        setDeleteModelOpen(false)
        RequestErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
      })
  }

  useEffect(() => {
    const transformedData = approvedData.map((item) => ({
      ...item,
      connStatus: item.sseConnectionDtl?.connStatus ?? null,
    }))

    setTransformedData(transformedData)
  }, [approvedData])

  const table = useMaterialReactTable({
    columns,
    data: transformedData,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
    },
    getRowId: (row) => String(row.terminalId ?? row.id ?? `${row.orgCode}:${row.serialNo}:${row.groupId ?? ""}`),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-actions"],
        right: ["status"],
      },
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    muiToolbarAlertBannerProps: false,
    rowCount: appRowCount,
    muiPaginationProps: { rowsPerPageOptions: [10] },
    editDisplayMode: "modal",
    enableColumnPinning: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowActions: true,
    enableRowSelection: true,
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem key="assignGroup" onClick={() => {
        setAssignmentTerminals([row.original])
        closeMenu()
      }}>
        <ListItemIcon><DriveFileMoveOutlinedIcon color="primary" /></ListItemIcon>
        Manage group
      </MenuItem>,
      <MenuItem
        key="moreStats"
        onClick={() => {
          setMoreInfoModalOpen(true)
          setRowData(row.original)
          closeMenu()
        }}
      >
        <ListItemIcon>
          <TroubleshootIcon color="primary" />
        </ListItemIcon>
        More Stats
      </MenuItem>,
      <MenuItem
        key="updateStatus"
        onClick={() => {
          setUpdateStatusModalOpen(true)
          setRowData(row.original)
          closeMenu()
        }}
      >
        <ListItemIcon>
          <SecurityUpdateWarningIcon color="warning" />
        </ListItemIcon>
        Update Status
      </MenuItem>,
    ],
    renderTopToolbarCustomActions: ({ table }) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Box sx={{ display: "flex", gap: "1rem" }}>
          <Button startIcon={<DriveFileMoveOutlinedIcon />} disabled={isLoading || !table.getSelectedRowModel().flatRows.length}
            onClick={() => setAssignmentTerminals(table.getSelectedRowModel().flatRows.map((row) => row.original))}>
            Manage group
          </Button>
          <Tooltip arrow placement="right" title="Delete">
            <IconButton
              color="error"
              disabled={!table.getSelectedRowModel().flatRows.length}
              onClick={() => {
                setRowsData([])
                table.getSelectedRowModel().flatRows.forEach((row) => {
                  setRowsData((prevData) => [...prevData, { ...row.original }])
                })
                setDeleteModelOpen(true)
                setTableState(table)
              }}
              variant="contained"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </div>
    ),
  })

  return (
    <div>
      <div>
        <Alerts
          iserror={iserror}
          errorMessages={errorMessages}
          isSuccess={isSuccess}
          successMessages={successMessages}
          iserrorShow={iserrorShow}
        />
      </div>
      <br />

      <Box sx={{ display: "grid" }}>
        <MaterialReactTable table={table} />
      </Box>
      <DeleteModal
        open={deleteModelOpen}
        onClose={() => setDeleteModelOpen(false)}
        onDelete={handleDelete}
        isBtnLoading={isBtnLoading}
      />
      {assignmentTerminals && <TerminalAssignmentModal
        terminals={assignmentTerminals}
        onClose={() => setAssignmentTerminals(null)}
        onSaved={(message) => {
          table.resetRowSelection()
          setIsSuccess(true)
          setSuccessMessages([message])
          setUpdateData(true)
        }}
      />}
      <ApproveTerminalsMoreInfoModal
        open={moreInfoModalOpen}
        onClose={() => setMoreInfoModalOpen(false)}
        approvedData={rowData}
      />
      <UpdateTmnlStatusModal
        open={updateStatusModalOpen}
        onClose={() => setUpdateStatusModalOpen(false)}
        terminalSerial={rowData.serialNo}
        setIsStatusChange={setIsStatusChange}
      />
    </div>
  )
}
export default ApprovedTerminals
